import { x } from '@xstyled/styled-components';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  useRef
} from 'react';
import { createPortal } from 'react-dom';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { usePhoneAudioEffect } from '~/ui/audio-effect/usePhoneAudioEffect';
import { Flex } from '~/ui/flex/Flex';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { shallow } from 'zustand/shallow';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { TrainingState, useTrainingStore } from '~/training/state';
import { EditorMediaFile, useEditorStore } from '~/shell/Store';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import useIsDarkMode from '~/lib/themes/hooks/useIsDarkMode';
import Avatar from './avatar.png';
import {
  OptionalFieldSettledContext,
  SettledOptionalField
} from '../text/optionalFieldSettled';
import { optionalFieldWrites } from '../text/optionalFieldWrites';
import { saveTargetOf } from '../text/helper/saveTarget';
import { CallerFields, callerFieldOf } from './callerLine';
import * as S from './styles';
import {
  getPhoneFitScale,
  getPhoneFrameSize,
  isPhoneMobileViewport,
  PHONE_FRAME_HEIGHT_PX,
  PHONE_FRAME_WIDTH_PX,
  PHONE_MOBILE_DESIGN_SCALE
} from './phoneReflowLayout';

const selectorTraining = (s: TrainingState) => select(s, 'logoUrl');
const selector = (s: MediaPlayerStore) =>
  select(s, 'pause', 'play', 'status', 'reset', 'loadMedia');

type CallerLine = React.ReactElement<{
  id: string;
  value?: string;
  emptyHint?: string;
}>;

/**
 * Whether the server holds a caller line's text, as far as it has confirmed:
 * after a slide redraw the editor's own copy may show a save that was refused.
 */
const savedHasText = (line: CallerLine) => {
  const { subitemId, field } = saveTargetOf(line.props.id);
  const writes = optionalFieldWrites(
    subitemId,
    field,
    Boolean(line.props.value)
  );
  writes.seen(Boolean(line.props.value));
  return writes.hasText();
};

/**
 * The editor's caller line: the line learners would see with what the server
 * holds, as each line's saves settle (see PhoneHelper). A refused save keeps
 * its line in view to try again, and the line the author is in stays until
 * they leave it. Every line stays mounted, so a hidden one keeps its text.
 */
const EditorCallerLine = ({
  lines: [titleLine, subtitleLine]
}: {
  lines: CallerLine[];
}) => {
  const [saved, setSaved] = useState<CallerFields>(() => ({
    title: savedHasText(titleLine),
    subtitle: subtitleLine ? savedHasText(subtitleLine) : false
  }));
  const [editing, setEditing] = useState<keyof CallerFields | null>(null);
  const onSettled = useCallback(
    ({ field, hasText }: SettledOptionalField) =>
      setSaved(current => ({ ...current, [field]: hasText })),
    []
  );
  const shown =
    editing ??
    (subtitleLine && callerFieldOf(saved) === 'subtitle'
      ? 'subtitle'
      : 'title');

  return (
    <OptionalFieldSettledContext.Provider value={onSettled}>
      <div
        hidden={shown !== 'title'}
        onFocus={() => setEditing('title')}
        onBlur={() => setEditing(null)}
      >
        {titleLine}
      </div>
      {subtitleLine && (
        <div
          hidden={shown !== 'subtitle'}
          onFocus={() => setEditing('subtitle')}
          onBlur={() => setEditing(null)}
        >
          {subtitleLine}
        </div>
      )}
    </OptionalFieldSettledContext.Provider>
  );
};

interface Props {
  id?: string;
  children: React.ReactNode;
  narration: string;
  subtitlesUrl?: string;
  autoPlay: boolean;
  name?: string;
  photo?: string;
  personalization?: boolean;
}

function getPhoneLayout(viewportWidth: number) {
  const frame = getPhoneFrameSize(viewportWidth);
  const isMobile = isPhoneMobileViewport(viewportWidth);
  const designScale = isMobile ? PHONE_MOBILE_DESIGN_SCALE : 1;
  const fitScale = getPhoneFitScale(viewportWidth, frame.width);
  const phoneTransformScale = designScale * fitScale;
  return {
    // Layout matches the visual size after transform (avoids clip / scroll).
    layoutWidth: PHONE_FRAME_WIDTH_PX * phoneTransformScale,
    layoutHeight: PHONE_FRAME_HEIGHT_PX * phoneTransformScale,
    /** Scale applied to the desktop-sized Phone node (design × WCAG fit). */
    phoneTransformScale,
    /** Keep tooltip at desktop visual size on mobile. */
    tooltipCounterScale: isMobile ? 1 / PHONE_MOBILE_DESIGN_SCALE : 1
  };
}

export const PhoneCall = ({
  id,
  children,
  narration,
  subtitlesUrl,
  name,
  photo,
  personalization = false
}: Props) => {
  const callerLines = (children as CallerLine[][])[0];
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const { logoUrl } = useTrainingStore(selectorTraining, shallow);
  const { contentEditable, editorMode, updateFile } = useEditorStore();
  const isDarkMode = useIsDarkMode();
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [phoneLayout, setPhoneLayout] = useState(() =>
    getPhoneLayout(typeof window !== 'undefined' ? window.innerWidth : Infinity)
  );

  const [hasStarted, setHasStarted] = useState(false);
  const startTimeoutRef = useRef<number | null>(null);
  const audioEffectPlayedRef = useRef<boolean>(false);
  const { play, pause, status, loadMedia } = useMediaPlayerStore(
    selector,
    shallow
  );

  const {
    audioEffectDuration,
    phoneAudioEffectIsPaused,
    pausePhoneAudioEffect
  } = usePhoneAudioEffect(editorMode);

  useGlobalMedia();

  // Desktop 248×503 (Figma 94:2680); mobile 205×414 (94:2681) + WCAG reflow.
  useLayoutEffect(() => {
    const updateLayout = () =>
      setPhoneLayout(getPhoneLayout(window.innerWidth));
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  useEffect(() => {
    pause();
  }, []);

  useEffect(() => {
    // Use setTimeout(0) to avoid audio loading issues during slide transitions.
    // This pushes loadMedia to the next event loop cycle, allowing React cleanup
    // from previous slides to complete before loading new media
    setTimeout(() => {
      loadMedia(narration, subtitlesUrl, 'audio', true, 0);
    }, 0);
  }, [narration, subtitlesUrl]);

  useEffect(() => {
    if (hasStarted) {
      pausePhoneAudioEffect();
    }
  }, [hasStarted]);

  useEffect(() => {
    // Pause only if playing started automatically (autoplay), not by user click
    if (status === 'playing' && !audioEffectPlayedRef.current && !hasStarted) {
      audioEffectPlayedRef.current = true;
      pause();
    }

    if (
      startTimeoutRef.current === null &&
      !phoneAudioEffectIsPaused &&
      !hasStarted &&
      status === 'paused'
    ) {
      startTimeoutRef.current = window.setTimeout(() => {
        setHasStarted(true);
        play();
      }, audioEffectDuration);
    }

    return () => {
      if (status === 'paused') {
        setHasStarted(true);
      }

      if (startTimeoutRef.current !== null) {
        clearTimeout(startTimeoutRef.current as number);
        startTimeoutRef.current = null;
      }
    };
  }, [status]);

  const UpdateFileElement = async (file: EditorMediaFile) => {
    if (id) {
      updateFile(id, file, 'PHONE');
    }
    setShowFilePicker(false);
  };

  return (
    <Flex h="100%" alignItems="center" justifyContent="center">
      {/*
        Layout box matches the *visual* size after scale — transform alone would
        leave a fixed flow width and still force horizontal scroll.
      */}
      <x.div
        position="relative"
        style={{
          width: phoneLayout.layoutWidth,
          height: phoneLayout.layoutHeight
        }}
      >
        <S.Phone
          style={
            phoneLayout.phoneTransformScale < 1
              ? {
                  width: PHONE_FRAME_WIDTH_PX,
                  height: PHONE_FRAME_HEIGHT_PX,
                  transform: `scale(${phoneLayout.phoneTransformScale})`,
                  transformOrigin: 'top left'
                }
              : undefined
          }
        >
          <S.Contact>
            <div
              ref={avatarRef}
              style={{ display: 'inline-block', position: 'relative' }}
            >
              {photo || (personalization && logoUrl) ? (
                <x.img
                  src={photo || logoUrl}
                  w="32px"
                  h="32px"
                  borderRadius="50px"
                  backgroundColor="#FFF"
                  alt={''}
                  onClick={
                    contentEditable ? () => setShowFilePicker(true) : undefined
                  }
                  style={
                    contentEditable
                      ? { cursor: 'pointer', objectFit: 'contain' }
                      : { objectFit: 'contain' }
                  }
                />
              ) : (
                <x.img
                  src={Avatar}
                  w="32px"
                  h="32px"
                  borderRadius="50px"
                  backgroundColor="#FFF"
                  alt={''}
                  onClick={
                    contentEditable ? () => setShowFilePicker(true) : undefined
                  }
                  style={contentEditable ? { cursor: 'pointer' } : undefined}
                />
              )}

              {showFilePicker &&
                id &&
                createPortal(
                  <FilePickerV2
                    fileElRef={avatarRef}
                    updateFile={UpdateFileElement}
                    type="image"
                    onClose={() => setShowFilePicker(false)}
                  />,
                  document.body
                )}
            </div>

            <S.UserName>
              {contentEditable && callerLines[0]?.props?.emptyHint ? (
                <EditorCallerLine lines={callerLines} />
              ) : callerLines[0]?.props?.value ? (
                children
              ) : (
                name
              )}
            </S.UserName>
          </S.Contact>

          <S.DeclineButton aria-hidden="true" />

          {/* `aria-labelledby` expects element ids (not text) and `tabindex > 0`
              breaks the natural tab order, so the button relies on its
              `aria-label` + `title` and the default button focusability. */}
          <S.CallButtonWrap>
            {!hasStarted && !editorMode && (
              <S.AcceptCallTooltip
                $isDark={isDarkMode}
                $counterScale={phoneLayout.tooltipCounterScale}
                role="tooltip"
              >
                {t`Click to answer`}
              </S.AcceptCallTooltip>
            )}
            <S.CallButton
              aria-label={t`Accept`}
              title={t`Click to answer`}
              onClick={() => {
                if (!hasStarted) {
                  audioEffectPlayedRef.current = true; // Prevent pause() in useEffect
                  play();
                  setHasStarted(true);
                }
              }}
            />
          </S.CallButtonWrap>
        </S.Phone>
      </x.div>

      <MediaCaptions />
    </Flex>
  );
};
